const MAX_HOSTNAME_LENGTH = 253;
const HOSTNAME_LABEL_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const TOOL_OPTIONS = [
  { value: 'itineraryAssist', label: 'Itinerary Assist' },
  { value: 'bookings', label: 'Booking Automation' },
];

const TOOL_LABELS = TOOL_OPTIONS.reduce((labels, option) => ({
  ...labels,
  [option.value]: option.label,
}), {});

export const INITIAL_MAPPING_DIALOG_VALUES = {
  tool: 'itineraryAssist',
  integrationId: '',
  hint: '',
  agentId: '',
  hostnamePattern: '',
};

export const normalizeMappingText = value => (value || '').trim();

export const createInitialMappingDialogState = () => ({
  open: false,
  index: null,
  values: {
    ...INITIAL_MAPPING_DIALOG_VALUES,
  },
  appKeys: [],
  appKeysLoading: false,
  appKeysError: '',
  lookupPartners: [],
  lookupPartnersLoading: false,
  localError: '',
  savePending: false,
});

export const getHintDisplayLabel = (hint, partners = []) => {
  const normalizedHint = normalizeMappingText(hint);
  if (!normalizedHint) return '';
  const matchedPartner = (partners || []).find(partner => partner?.id === normalizedHint);
  return matchedPartner?.name || normalizedHint;
};

export const getResolvedPartners = (allPartners = [], lookupPartners = []) => {
  const partnerMap = {};
  [...(allPartners || []), ...(lookupPartners || [])].forEach(partner => {
    if (!partner || !partner.id) return;
    partnerMap[partner.id] = partner;
  });
  return Object.values(partnerMap);
};

export const getHintsForIntegration = (allAppKeys = [], integrationId = '') => Array.from(new Set(
  allAppKeys
    .filter(appKey => appKey.integrationId === integrationId)
    .map(appKey => appKey.hint)
    .filter(hint => typeof hint === 'string' && hint.trim())
)).sort();

export const getUniqueIntegrationIds = (allAppKeys = [], currentIntegrationId = '') => {
  const integrationIds = Array.from(new Set(
    allAppKeys
      .map(appKey => appKey.integrationId)
      .filter(Boolean)
  ));
  if (currentIntegrationId && integrationIds.indexOf(currentIntegrationId) === -1) {
    integrationIds.push(currentIntegrationId);
  }
  return integrationIds.sort();
};

const isValidHostname = hostname => {
  if (!hostname || hostname.length > MAX_HOSTNAME_LENGTH) return false;
  const hostnameParts = hostname.split('.');
  if (hostnameParts.length < 2) return false;
  return hostnameParts.every(part => HOSTNAME_LABEL_REGEX.test(part));
};

export const normalizeHostnamePattern = value => {
  const trimmed = normalizeMappingText(value).toLowerCase();
  if (!trimmed) return '';
  if (trimmed.includes('://')) {
    throw Error('Hostname pattern must be a hostname only, not a full URL.');
  }
  if (/[/?#:]/.test(trimmed)) {
    throw Error('Hostname pattern must not include paths, ports, queries, or fragments.');
  }
  if (trimmed.startsWith('*.')) {
    const suffix = trimmed.slice(2);
    if (!isValidHostname(suffix)) {
      throw Error('Wildcard hostname patterns must use the "*.example.com" format.');
    }
    return `*.${suffix}`;
  }
  if (!isValidHostname(trimmed)) {
    throw Error('Hostname pattern must be a valid hostname like "supplier.example.com".');
  }
  return trimmed;
};

export const normalizeMappingForSave = mapping => {
  const normalizedTool = normalizeMappingText(mapping?.tool);
  const normalizedIntegrationId = normalizeMappingText(mapping?.integrationId);
  const normalizedHint = normalizeMappingText(mapping?.hint);
  const normalizedAgentId = normalizeMappingText(mapping?.agentId);
  const normalizedHostnamePattern = normalizeHostnamePattern(mapping?.hostnamePattern);
  return {
    ...(normalizedTool ? { tool: normalizedTool } : {}),
    ...(normalizedIntegrationId ? { integrationId: normalizedIntegrationId } : {}),
    ...(normalizedHint ? { hint: normalizedHint } : {}),
    ...(normalizedAgentId ? { agentId: normalizedAgentId } : {}),
    ...(normalizedHostnamePattern ? { hostnamePattern: normalizedHostnamePattern } : {}),
  };
};

export const extractTi2AppKeys = response => {
  const rawAppKeys = Array.isArray(response)
    ? response
    : (response?.userAppKeys || response?.integrations || []);
  if (!Array.isArray(rawAppKeys)) return [];
  return rawAppKeys
    .map(appKey => {
      const integrationId = appKey?.integrationId || appKey?.app || '';
      const hint = appKey?.hint || appKey?.tokenHint || '';
      if (!integrationId) return null;
      return {
        ...appKey,
        integrationId,
        hint,
      };
    })
    .filter(Boolean);
};

export const getMappingKey = mapping =>
  `${mapping?.tool || ''}::${mapping?.integrationId || ''}::${mapping?.hint || ''}::${mapping?.agentId || ''}::${mapping?.hostnamePattern || ''}`;

export const buildLocalValidationError = ({ mapping, mappings = [], editingIndex }) => {
  if (!mapping.tool) return 'Tool is required.';
  if (!mapping.integrationId) return 'Integration is required.';
  if (!mapping.hostnamePattern) return '';
  const mappingKey = `${mapping.tool}::${mapping.hostnamePattern}`;
  const duplicateIndex = mappings.findIndex((existingMapping, index) => {
    if (index === editingIndex) return false;
    const existingTool = normalizeMappingText(existingMapping?.tool);
    const existingHostnamePattern = normalizeMappingText(existingMapping?.hostnamePattern);
    return existingTool
      && existingHostnamePattern
      && `${existingTool}::${existingHostnamePattern.toLowerCase()}` === mappingKey;
  });
  if (duplicateIndex > -1) {
    return `This partner already has a ${mapping.tool} mapping for ${mapping.hostnamePattern}.`;
  }
  return '';
};

export const hasResolvablePartnerHints = mappings =>
  Array.isArray(mappings)
    && mappings.some(mapping => normalizeMappingText(mapping?.hint));

export const hasMissingPartnerHintLabels = (mappings = [], partners = []) => {
  if (!hasResolvablePartnerHints(mappings)) return false;
  const partnerIds = new Set((partners || []).map(partner => partner?.id).filter(Boolean));
  return mappings.some(mapping => {
    const hint = normalizeMappingText(mapping?.hint);
    if (!hint) return false;
    return !partnerIds.has(hint);
  });
};

export const humanizeToolName = toolName => {
  const normalizedToolName = toolName || 'unknown';
  if (TOOL_LABELS[normalizedToolName]) return TOOL_LABELS[normalizedToolName];
  return normalizedToolName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, chr => chr.toUpperCase());
};
